import React from "react";
import { ExternalLink } from "lucide-react";

/**
 * Cinematic partnership credibility band.
 * Used on Landing (full band) and Dashboard (compact card variant).
 */

const JOBVIBE_URL = "https://www.jobvibe.co.uk";

export const PartnerBand = () => (
  <section
    className="border-t border-warm-ivory/8"
    data-testid="partner-band"
  >
    <div className="max-w-[1480px] mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
      <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-10 lg:gap-16 items-center">
        <div>
          <div className="eyebrow-gold mb-4">In partnership with</div>
          <h3 className="font-display text-3xl sm:text-4xl tracking-[-0.02em] leading-[1.05] text-warm-ivory">
            Built for candidates.{" "}
            <span className="editorial-accent">Powered for hire.</span>
          </h3>
          <p className="text-warm-ivory/55 text-base leading-relaxed mt-5 max-w-md">
            Once your resume is polished, take it where recruiters are looking.
            PrestiqueResume is the official identity partner of{" "}
            <span className="text-warm-ivory">JobVibe</span> — the UK job
            board connecting ambitious candidates with hand-picked roles.
          </p>
          <a
            href={JOBVIBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partner-band-cta"
            className="mt-7 inline-flex items-center gap-2 font-mono uppercase tracking-[0.18em] text-[11px] text-burnished-gold underline underline-offset-8 decoration-burnished-gold/40 hover:decoration-burnished-gold transition-colors"
          >
            Browse roles on JobVibe
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div
          className="hidden lg:block w-px h-32 bg-warm-ivory/10"
          aria-hidden="true"
        />

        <div className="flex justify-start lg:justify-center">
          <a
            href={JOBVIBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit JobVibe"
            className="group block relative"
            data-testid="partner-band-logo"
          >
            <div
              className="absolute -inset-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(circle, rgba(176,141,87,0.10), transparent 70%)",
              }}
              aria-hidden="true"
            />
            <img
              src="/partners/jobvibe.jpg"
              alt="JobVibe — UK job board"
              className="relative w-44 h-44 lg:w-52 lg:h-52 rounded-full object-cover ring-1 ring-warm-ivory/10 group-hover:ring-burnished-gold/40 transition-all duration-500 will-change-transform group-hover:scale-[1.02]"
              loading="lazy"
            />
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* Compact dashboard card variant — surfaced post-export */
export const PartnerCard = () => (
  <a
    href={JOBVIBE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="partner-card"
    className="bento-card p-6 flex items-center gap-5 group hover:border-burnished-gold/40 transition-colors"
  >
    <img
      src="/partners/jobvibe.jpg"
      alt=""
      aria-hidden="true"
      className="w-14 h-14 rounded-full object-cover ring-1 ring-warm-ivory/10 shrink-0"
      loading="lazy"
    />
    <div className="flex-1 min-w-0">
      <div className="eyebrow-gold mb-1.5">Next step · partner</div>
      <div className="font-display text-lg leading-tight text-warm-ivory">
        Your CV is ready — find your next role on JobVibe
      </div>
      <p className="text-warm-ivory/55 text-sm mt-1">
        Browse hand-picked openings from JobVibe, our official hiring partner.
      </p>
    </div>
    <ExternalLink className="w-4 h-4 text-burnished-gold shrink-0 group-hover:translate-x-0.5 transition-transform" />
  </a>
);

export default PartnerBand;
